<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore.js';
	import MarketGroupSetStore from '@/Stores/Sport/MarketGroupSetStore.js';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Icon from '@/Components/Icon.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Switch from '@/Components/Switch.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { removeUrlParameter, setUrlParameter } from '@/Services/Helpers';

	export let id;
	export let sportId;

	MarketGroupStore.resetStore();

	let sending = false;
	let selectedSport = sportId;
	let loading =
		SportStore.getSports() && id === 'create'
			? MarketGroupSetStore.newMarketGroupSet()
			: MarketGroupSetStore.getMarketGroupSet(id).then(() => (selectedSport = $MarketGroupSetStore.sport.id));

	$: selectedSport && selectedSportChanged();
	$: selectedMarketGroups = $MarketGroupSetStore && $MarketGroupSetStore.market_groups;

	function onClickSave() {
		if (!$MarketGroupSetStore.name || !selectedSport) {
			notify({
				type: 'danger',
				title: 'Error!',
				message: 'Market Group Set must have a name and sport',
				timeout: 3
			});
			return;
		}

		sending = true;
		MarketGroupSetStore.saveMarketGroupSet()
			.then(() => {
				notify({
					type: 'success',
					title: $MarketGroupSetStore.name,
					message: 'Market Group Set Saved',
					timeout: 3
				});
				if (id === 'create') {
					navigate(`/sport/markets/group-sets${selectedSport ? '?sport=' + selectedSport : ''}`);
				}
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to save Market Group Set',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function selectedSportChanged() {
		MarketGroupSetStore.updateSelectedSport(selectedSport);
		MarketGroupStore.getMarketGroupsForSport(selectedSport);
	}

	function moveMarketGroupUp(marketGroup) {
		let mg = $MarketGroupSetStore.market_groups;
		let neighbour = mg.sortBy('order').where('order', '<', marketGroup.order).last();
		if (neighbour) {
			MarketGroupSetStore.swapMarketGroupOrder(marketGroup, neighbour);
		}
	}

	function moveMarketGroupDown(marketGroup) {
		let mg = $MarketGroupSetStore.market_groups;
		let neighbour = mg.sortBy('order').where('order', '>', marketGroup.order).first();
		if (neighbour) {
			MarketGroupSetStore.swapMarketGroupOrder(marketGroup, neighbour);
		}
	}

	function changeSport(sport_id) {
		if (typeof parseInt(sport_id) !== 'undefined' && sport_id !== false) {
			setUrlParameter('sport', sport_id);
			sportId = sport_id;
		} else {
			removeUrlParameter('sport');
			sportId = false;
		}
	}
</script>

<div>
	{#await loading}
		<Loading />
	{:then}
		{#if $SportStore && $MarketGroupSetStore}
			<div>
				<!-- Page Header -->
				<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
					<div class="flex items-center w-full">
						<a
							href={`/sport/markets/group-sets${selectedSport ? '?sport=' + selectedSport : ''}`}
							title="Market Group Sets"
							class="flex cursor-pointer hover:underline text-grey items-center"
						>
							<Icon name="chevron-left" classes="w-2 mr-1" />
							<div class="mr-6 text-xl font-bold">Market Group Sets</div>
						</a>
						<h1 class="flex-1 mr-2 text-xl font-bold">
							<input
								type="text"
								value={$MarketGroupSetStore.name}
								placeholder="Market Group Set Name"
								on:change={(e) => MarketGroupSetStore.updateName(e.target.value)}
								class="w-1/2 font-semibold border-b focus:outline-none"
							/>
						</h1>
						<div class="flex items-center space-x-6">
							{#if selectedSport}
								<div class="flex flex-row justify-center items-center mr-4">
									<Switch
										name="isDefault"
										checked={$MarketGroupSetStore.is_default}
										on:change={(e) => MarketGroupSetStore.updateIsDefault(e.target.checked)}
									/>
									<div class="ml-1 font-bold">
										Default for {$SportStore.where('id', selectedSport).pluck('name').first()}
									</div>
								</div>
							{/if}
							<div class="flex items-center">
								{#if $ACLStore && ACLStore.granted('market_group_sets.update')}
									<LoadingButton className="btn btn-black" on:click={onClickSave} loading={sending}>Save</LoadingButton>
								{/if}
							</div>
						</div>
					</div>
				</div>
				<!-- /Page Header -->
				<div class="p-2 mx-4 bg-gray-100 rounded">
					{#if id === 'create'}
						<SelectInput
							className="py-1 w-1/3"
							name="sport"
							required={true}
							bind:value={selectedSport}
							on:change={(event) => changeSport(event.target.value)}
						>
							<option value={false}>Select Sport...</option>
							{#each $SportStore.sortBy('name').all() as sport (sport.id)}
								{#if parseInt(sport.id) === parseInt(selectedSport)}
									<option value={sport.id} selected>{sport.name}</option>
								{:else}
									<option value={sport.id}>{sport.name}</option>
								{/if}
							{/each}
						</SelectInput>
					{:else}
						<div class="font-semibold">
							Sport: {$SportStore.where('id', selectedSport).pluck('name').first()}
						</div>
					{/if}
					<div class="w-full">
						<div class="my-4">
							<div class="flex w-full space-x-2">
								<!-- MARKETS GROUPS SELECTED -->
								<div class="w-2/3">
									{#if selectedMarketGroups}
										<div class="flex items-center w-full mb-2 space-x-4">
											<div class="font-semibold">
												Market Groups in this set ({selectedMarketGroups.count()})
											</div>
											<button
												on:click={() => MarketGroupSetStore.resetMarketGroups()}
												class="py-1 -mb-1 text-xs font-normal btn btn-primary-action"
											>
												Clear
											</button>
										</div>
										<div class="w-full h-screen p-1 overflow-y-auto bg-gray-200 border">
											{#if selectedMarketGroups.count() === 0}
												<div class="py-4 mx-2 text-center">Add some market groups to this set...</div>
											{/if}
											{#each selectedMarketGroups.sortBy('order').all() as marketGroup}
												<div class="flex items-center w-full space-x-2 hover:bg-gray-300">
													<div class="flex flex-col -mr-1 text-white bg-green-500 rounded-full">
														<button
															on:click={() => moveMarketGroupUp(marketGroup)}
															class="px-1 rounded-t-full focus:outline-none hover:bg-green-600"
														>
															<Icon name="chevron-up" classes="w-2 h-4" />
														</button>
														<button
															on:click={() => moveMarketGroupDown(marketGroup)}
															class="px-1 rounded-b-full focus:outline-none hover:bg-green-600"
														>
															<Icon name="chevron-down" classes="w-2 h-4" />
														</button>
													</div>
													<PillMultiSelect
														action="remove"
														on:remove={() => MarketGroupSetStore.removeMarketGroupId(marketGroup.market_group_id)}
														bgColour="bg-green-500"
														label={marketGroup.name}
													/>
													<div class="flex justify-end flex-1 space-x-4">
														<CheckBox bind:checked={marketGroup.is_key} size="4" text="Key Market" />
														<CheckBox bind:checked={marketGroup.is_primary} size="4" text="Primary" />
														<CheckBox bind:checked={marketGroup.default_open} size="4" text="Open" />
													</div>
												</div>
											{/each}
										</div>
									{/if}
									<div class="flex items-center justify-around w-full mt-1 text-xs italic">
										<div>
											*
											<span class="font-semibold">Key Market:</span>
											Not shown to users, key markets for trading.
										</div>
										<div>
											<span class="font-semibold">Primary:</span>
											Markets shown on event list and top of event.
										</div>
										<div>
											<span class="font-semibold">Open:</span>
											Market group will start open.
										</div>
									</div>
								</div>
								<!-- AVAILABLE MARKET GROUPS TO SELECT FROM -->
								<div class="w-1/3">
									<div class="flex items-center justify-between w-full mb-2">
										<div class="font-semibold">
											Available Market Groups
											{#if selectedSport}
												({`${$MarketGroupStore.count() - selectedMarketGroups.count()}/${$MarketGroupStore.count()}`})
											{/if}
										</div>
									</div>
									<div class="w-full h-screen p-2 overflow-y-auto bg-gray-200 border">
										{#if selectedSport}
											{#each $MarketGroupStore
												.whereNotIn('id', selectedMarketGroups.pluck('market_group_id').toArray())
												.all() as marketGroup}
												<div class="w-full">
													<PillMultiSelect
														on:add={() => MarketGroupSetStore.addMarketGroup(marketGroup)}
														bgColour="bg-green-500"
														label={marketGroup.name}
													/>
												</div>
											{/each}
										{/if}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		{/if}
	{/await}
</div>
